/**
 * ConditionInfluencePrompt — iter760 (Feb 2026)
 *
 * BHI-embedded prompt banner that:
 *   1. Shows how many structured conditions the user has entered
 *   2. Explains that these conditions ARE now used by BHI's metabolic
 *      environment and by the 18 Digital Twins to sharpen accuracy
 *   3. Provides a one-click deep link to add/manage conditions in Profile
 *
 * Auto-refreshes when conditions-changed events fire.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Stethoscope, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/App";
import { useHealthEvent } from "@/utils/healthEvents";

export default function ConditionInfluencePrompt({ isDark = false, lang = "bg" }) {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(null);
  const [lastDelta, setLastDelta] = useState(null);   // iter761 — persistent impact strip

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/health-conditions`);
      setCount(Number(data?.count || 0));
    } catch { setCount(0); }
  }, [API, user]);

  useEffect(() => { load(); }, [load]);
  useHealthEvent("conditions-changed", (detail) => {
    load();
    // iter761 — surface the impact-delta computed by withScoreDelta into
    // a persistent strip so the change stays visible after the toast fades.
    if (detail?.delta && !detail.delta.unchanged) {
      setLastDelta({ ...detail.delta, kind: detail.delta.kind || (detail.removed?.length ? "remove" : "add"), at: Date.now() });
    }
  });

  if (!user) return null;
  const bg = lang !== "en";
  const zero = count === 0 || count === null;

  const cls = zero
    ? (isDark
        ? "bg-gradient-to-r from-pink-500/15 to-purple-500/15 border-pink-500/40"
        : "bg-gradient-to-r from-pink-50 to-purple-50 border-pink-300")
    : (isDark
        ? "bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border-emerald-500/40"
        : "bg-gradient-to-r from-emerald-50 to-emerald-50/40 border-emerald-300");

  return (
    <div
      data-testid="bhi-condition-influence-prompt"
      className={`mb-5 p-4 rounded-xl border-l-4 flex items-start gap-3 ${cls}`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
        zero
          ? (isDark ? "bg-pink-500/25 text-pink-300" : "bg-pink-100 text-pink-700")
          : (isDark ? "bg-emerald-500/25 text-emerald-300" : "bg-emerald-100 text-emerald-700")
      }`}>
        <Stethoscope className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-[10px] uppercase font-bold tracking-widest mb-0.5 ${
          zero
            ? (isDark ? "text-pink-300" : "text-pink-700")
            : (isDark ? "text-emerald-300" : "text-emerald-700")
        }`}>
          {bg ? "Вашите диагнози в BHI и Digital Twins" : "Your diagnoses in BHI + Digital Twins"}
        </div>
        <h3 className={`text-sm font-bold mb-1 ${isDark ? "text-slate-100" : "text-slate-900"}`}>
          {zero
            ? (bg ? "Добавете вашите диагнози, за да заработи BHI на пълни обороти"
                  : "Add your structured diagnoses to activate BHI's full precision")
            : (bg ? `Използваме ${count} структурирано ${count === 1 ? "състояние" : "състояния"} за BHI и Twin-ите ви`
                  : `Using ${count} structured condition${count === 1 ? "" : "s"} in BHI + your 20 twins`)}
        </h3>
        <p className={`text-[12px] leading-relaxed sm:columns-2 sm:gap-6 [&>b]:font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
          {bg ? (
            <>
              Структурираните диагнози (напр. <b>PCOS</b>, <b>Хашимото</b>, <b>инсулинова резистентност</b>, <b>BRCA1/2</b>, <b>ендометриоза</b>, <b>NAFLD</b>)
              се използват директно от <b>Метаболитната среда на BHI</b> (естроген, инсулин, възпаление), от свързаните
              <b> Дигитални близнаци</b> (хормонален, метаболитно-инсулинов, възпалителен, чернодробно-бъбречен), а BHI композитният скор
              <b> се влива обратно</b> в тези близнаци с тегло 15% — така промяната се вижда мигновено, без презареждане.
            </>
          ) : (
            <>
              Structured diagnoses (e.g. PCOS, Hashimoto, insulin resistance, BRCA1/2, endometriosis, NAFLD) are used directly by BHI&apos;s
              Metabolic Environment and by the related Digital Twins (hormonal, metabolic-insulin, inflammation, liver-kidney).
              The composite BHI score is blended back into those twins (weight 15%) so the change reflects instantly, without a reload.
            </>
          )}
        </p>
        {!zero && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px]">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            <span className={isDark ? "text-emerald-300" : "text-emerald-700"}>
              {bg ? "Промените се пресмятат автоматично при добавяне/премахване." : "Recomputes automatically when you add/remove a condition."}
            </span>
          </div>
        )}
        {/* iter761 — Persistent impact-delta strip (last change) */}
        {lastDelta && !lastDelta.unchanged && (
          <div
            data-testid="bhi-impact-delta-strip"
            className={`mt-2 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] px-2.5 py-1.5 rounded-lg ${
              lastDelta.kind === "remove"
                ? (isDark ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-500/40" : "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-300")
                : (isDark ? "bg-amber-500/15 text-amber-200 ring-1 ring-amber-500/40"   : "bg-amber-50   text-amber-800   ring-1 ring-amber-300")
            }`}
          >
            <span className="font-bold">
              {bg ? "Последна промяна:" : "Last change:"}
            </span>
            <span data-testid="bhi-impact-delta-message">{lastDelta.message}</span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          // iter811 — role-aware profile route (doctor/admin → /doctor/profile,
          // patient → /dashboard/profile). Uses SPA navigate() so hash is
          // preserved, then scrolls to the anchor which now lives inside
          // HealthConditionsCard itself (both pages host it).
          const role = String(user?.role || "").toLowerCase();
          const path = (role === "doctor" || role === "admin")
            ? "/doctor/profile"
            : "/dashboard/profile";
          navigate(`${path}#health-conditions`);
          setTimeout(() => {
            document.getElementById("health-conditions")?.scrollIntoView({ behavior: "smooth" });
          }, 350);
        }}
        data-testid="bhi-add-conditions-cta"
        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold whitespace-nowrap ${
          zero
            ? "bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600"
            : (isDark ? "bg-emerald-500/25 text-emerald-200 hover:bg-emerald-500/35" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200")
        }`}
      >
        <Sparkles className="w-3.5 h-3.5" />
        {zero
          ? (bg ? "Добави сега" : "Add now")
          : (bg ? "Управлявай" : "Manage")}
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
